import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { submitForm } from "@/lib/api";
import { Loader2 } from "lucide-react";

const inputBase =
  "w-full rounded-lg bg-[#0f0f10] border border-white/10 px-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#FE7200] focus:ring-2 focus:ring-[#FE7200]/30 transition-colors";

export default function SiteVisitForm({
  source = "site-visit",
  variant = "full",
  testIdPrefix = "site-visit",
}) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company_name: "",
    website: "",
    position: "",
    phone: "",
    employees: "",
    people_on_premises: "",
    company_address: "",
    preferred_date: "",
    preferred_time: "",
    machine_location: "",
    notes: "",
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      // Normalise the website field: if user types "example.com" or
      // "www.example.com" (no protocol), auto-prepend https:// so the
      // stored value is still a clickable, valid URL.
      const w = (form.website || "").trim();
      const website = w && !/^https?:\/\//i.test(w) ? `https://${w}` : w;
      await submitForm({ ...form, website, source });
      navigate("/thank-you");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Something went wrong. Please try again or email info.nector@gmail.com."
      );
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid={`${testIdPrefix}-form`}
      className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-8"
    >
      <h3 className="font-display text-2xl md:text-3xl font-bold text-white">
        Book a Free Site Visit
      </h3>
      <p className="mt-2 text-sm text-zinc-400">
        Fill this out and we'll confirm eligibility and contact you to arrange the best time.
      </p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Full Name" required>
          <input
            required
            data-testid={`${testIdPrefix}-full-name-input`}
            value={form.full_name}
            onChange={update("full_name")}
            placeholder="e.g. Jane Doe"
            className={inputBase}
          />
        </Field>
        <Field label="Email" required>
          <input
            required
            type="email"
            data-testid={`${testIdPrefix}-email-input`}
            value={form.email}
            onChange={update("email")}
            placeholder="you@company.com"
            className={inputBase}
          />
        </Field>
        <Field label="Company Name" required>
          <input
            required
            data-testid={`${testIdPrefix}-company-name-input`}
            value={form.company_name}
            onChange={update("company_name")}
            placeholder="Company / business name"
            className={inputBase}
          />
        </Field>
        <Field label="Website / Social Media Link">
          <input
            type="text"
            data-testid={`${testIdPrefix}-website-input`}
            value={form.website}
            onChange={update("website")}
            placeholder="e.g. example.com or facebook.com/yourpage (optional)"
            className={inputBase}
          />
        </Field>
        <Field label="Position / Title" required>
          <input
            required
            data-testid={`${testIdPrefix}-position-input`}
            value={form.position}
            onChange={update("position")}
            placeholder="e.g. Office Manager"
            className={inputBase}
          />
        </Field>
        <Field label="Phone Number" required>
          <input
            required
            type="tel"
            data-testid={`${testIdPrefix}-phone-input`}
            value={form.phone}
            onChange={update("phone")}
            placeholder="04xx xxx xxx"
            className={inputBase}
          />
        </Field>
        <Field label="Employees / Foot traffic (daily)" required>
          <input
            required
            data-testid={`${testIdPrefix}-employees-input`}
            value={form.employees}
            onChange={update("employees")}
            placeholder="e.g. 50"
            className={inputBase}
          />
        </Field>
        <Field label="Exact People on Premises" required>
          <input
            required
            data-testid={`${testIdPrefix}-people-input`}
            value={form.people_on_premises}
            onChange={update("people_on_premises")}
            placeholder="e.g. 45"
            className={inputBase}
          />
        </Field>
        <Field label="Company Site Address" required className="md:col-span-2">
          <input
            required
            data-testid={`${testIdPrefix}-address-input`}
            value={form.company_address}
            onChange={update("company_address")}
            placeholder="Street, suburb, postcode"
            className={inputBase}
          />
        </Field>
        <Field label="Preferred Site Visit Date" required>
          <input
            required
            type="date"
            data-testid={`${testIdPrefix}-date-input`}
            value={form.preferred_date}
            onChange={update("preferred_date")}
            className={inputBase}
          />
        </Field>
        <Field label="Preferred Site Visit Time" required>
          <input
            required
            type="time"
            data-testid={`${testIdPrefix}-time-input`}
            value={form.preferred_time}
            onChange={update("preferred_time")}
            className={inputBase}
          />
        </Field>
        <Field label="Where would you like the machine to go?" required className="md:col-span-2">
          <select
            required
            data-testid={`${testIdPrefix}-location-select`}
            value={form.machine_location}
            onChange={update("machine_location")}
            className={inputBase}
          >
            <option value="">Select an option</option>
            <option>Break room / kitchen</option>
            <option>Reception / lobby</option>
            <option>Warehouse / production floor</option>
            <option>Office hallway</option>
            <option>Gym / fitness area</option>
            <option>Hospital / clinic waiting area</option>
            <option>School / university common area</option>
            <option>Other (mention in notes)</option>
          </select>
        </Field>
        <Field label="Additional Notes / Questions" className="md:col-span-2">
          <textarea
            rows={5}
            data-testid={`${testIdPrefix}-notes-input`}
            value={form.notes}
            onChange={update("notes")}
            placeholder="Access notes, stairs/forklift, opening hours, product preferences, etc."
            className={inputBase}
          />
        </Field>
      </div>

      {error && (
        <div
          data-testid={`${testIdPrefix}-error-message`}
          className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200"
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid={`${testIdPrefix}-submit-button`}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FE7200] px-6 py-4 font-display font-bold uppercase tracking-[0.18em] text-sm text-black hover:bg-[#FF8C2E] disabled:opacity-60 transition-colors"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        {submitting ? "Submitting…" : "Submit Site Visit Request"}
      </button>

      <p className="mt-4 text-xs text-zinc-500 text-center">
        By submitting, you agree to be contacted about your site visit booking. No lock-in contracts.
      </p>
    </form>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-semibold tracking-wide text-zinc-300 mb-1.5">
        {label} {required && <span className="text-[#FE7200]">*</span>}
      </span>
      {children}
    </label>
  );
}
